import {
  InputChangeEventDetail,
  IonButton,
  IonButtons,
  IonFooter,
  IonIcon,
  IonInput,
  IonLabel,
  IonText,
} from '@ionic/react';
import classNames from 'classnames';
import { calendarClearOutline } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Datepicker from '@/components/common/Datepicker/Datepicker';
import { FilterCalendarProps, typesFilters } from '@/interfaces/FilterCalendarInterface/FilterCalendarInterface';
import { InitialStatus, Status } from '@/interfaces/OperationsDashboardInterface/OperationsDashboardInterface';
import { saveCartId, saveFilters, saveProposalRef } from '@/redux/filters/filters';
import { storageService } from '@/services/storage';
import { RootState } from '@/store';
import { DateHelper } from '@/utils/dateHelper';

import styles from './FilterCalendar.module.scss';

export default function FilterCalendar({
  dateStart,
  dateEnd,
  proposalReference,
  cartID,
  closeModal,
  updateFilterValues,
  currentButtons,
  updateCurrentButtons,
}: FilterCalendarProps) {
  const dispatch = useDispatch();
  const startDateFilter = useSelector(
    (state: RootState) => state.filters.startDate,
  );
  const endDateFilter = useSelector(
    (state: RootState) => state.filters.endDate,
  );
  const proposalRefFilter = useSelector(
    (state: RootState) => state.filters.proposalReference,
  );
  const cartFilter = useSelector((state: RootState) => state.filters.cartId);

  const existingEcoOffers = useSelector(
    (state: RootState) => state.commercialOffers.data.existingEcoOffers,
  );

  const [openCalendarStartDate, setOpenCalendarStartDate] = useState<boolean>(false);
  const [openCalendarEndDate, setOpenCalendarEndDate] = useState<boolean>(false);
  const [startDateInput, setStartDateInput] = useState<string | null | false>(
    startDateFilter,
  );
  const [endDateInput, setEndDateInput] = useState<string | null | false>(
    endDateFilter,
  );
  const [proposalRefInput, setProposalRefInput] = useState<string | null>(
    proposalRefFilter,
  );
  const [cartIdInput, setCartIdInput] = useState<string | null>(cartFilter);
  const [startDatePicker, setStartDatePicker] = useState<Date | null>(null);
  const [endDatePicker, setEndDatePicker] = useState<Date | null>(null);
  const [selectedButton, setSelectedButton] = useState<string>('');
  // const [proposalRefValue, setProposalRefValue] = useState<string>('');
  const [cookieProposalReference, setCookieProposalReference] = useState(storageService.hasAGCode());

  const [buttons, setButtons] = useState<Status[]>(currentButtons);
  const [filtersSelected, setFiltersSelected] = useState<Partial<typesFilters>>();

  useEffect(() => {
    const newValue = storageService.hasAGCode();
    if (cookieProposalReference !== newValue) {
      setCookieProposalReference(newValue);
    }
  }, [storageService.hasAGCode()]);

  const handleToday = () => {
    setSelectedButton('today');
    const actualDate = new Date();
    const day = actualDate.getDate();
    const month = actualDate.getMonth() + 1;
    const year = actualDate.getFullYear();
    const formatedMonth = month.toString().padStart(2, '0');
    const formatedDay = day.toString().padStart(2, '0');
    const fomartedDate = `${year}-${formatedMonth}-${formatedDay}`;
    dateStart(fomartedDate);
    dateEnd(fomartedDate);
    setStartDateInput(fomartedDate);
    setEndDateInput(fomartedDate);
  };

  const handleCurrentMonth = () => {
    setSelectedButton('currentMonth');
    const actualMonth = new Date();
    const primerDiaMes = new Date(
      actualMonth.getFullYear(),
      actualMonth.getMonth(),
      1,
    );
    const fechaActual = new Date();

    const firstDayFormated = primerDiaMes.toLocaleDateString('fr-CA');
    const actualDateFormated = fechaActual.toLocaleDateString('fr-CA');

    dateStart(firstDayFormated);
    dateEnd(actualDateFormated);
    setStartDateInput(firstDayFormated);
    setEndDateInput(actualDateFormated);
  };

  const handleLastMonth = () => {
    setSelectedButton('lastMonth');
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastDayOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);

    const firstDayMonth = lastMonth.toLocaleDateString('fr-CA');
    const LastDayMonth = lastDayOfLastMonth.toLocaleDateString('fr-CA');

    dateStart(firstDayMonth);
    dateEnd(LastDayMonth);

    setStartDateInput(firstDayMonth);
    setEndDateInput(LastDayMonth);
  };

  const handleApliedFilter = () => {
    startDateInput && dateStart(startDateInput);
    endDateInput && dateEnd(endDateInput);
    proposalRefInput && proposalRefInput.length > 3
      ? proposalReference(proposalRefInput)
      : proposalReference('');
    cartIdInput ? cartID(cartIdInput) : cartID('');
    updateFilterValues(filtersSelected!);
    updateCurrentButtons(buttons);
    dispatch(saveFilters(buttons));
    dispatch(saveCartId(cartIdInput || ''));
    dispatch(saveProposalRef(proposalRefInput || ''));
    closeModal(true);
  };

  const updateButtonsWithSelectedStatus = (
    buttons: Status[],
    initials: InitialStatus,
    isSelected: boolean,
  ): Status[] => buttons.map((button) => ({
    ...button,
    isSelected:
        button.initials === initials
          ? isSelected
          : button.initials === InitialStatus.Todas
            ? false
            : button.isSelected,
  }));

  const selectButtons = async (
    initials: InitialStatus,
    isSelected: boolean,
  ) => {
    let updatedButtons: Status[] = [];
    if (initials === InitialStatus.Todas) {
      updatedButtons = buttons.map((button) => ({
        ...button,
        isSelected: button.initials === initials,
      }));
    } else {
      updatedButtons = updateButtonsWithSelectedStatus(
        buttons,
        initials,
        isSelected,
      );
      updatedButtons
        .filter((button) => button.isSelected)
        .map((button) => button.initials);
    }
    setButtons(updatedButtons);

    const selectedButtonValues = updatedButtons
      .filter((button) => button.isSelected)
      .map((button) => {
        if (button.initials === InitialStatus.Todas) {
          return null;
        }
        return `&business_status=${button.initials}`;
      })
      .filter((value) => value !== null)
      .join('');

    setFiltersSelected({ buttons: selectedButtonValues, offset: 0 });
    currentButtons = updatedButtons;
  };

  const clearFilters = () => {
    setOpenCalendarStartDate(false);
    setOpenCalendarEndDate(false);
    dateStart('');
    dateEnd('');
    setStartDateInput('');
    setEndDateInput('');
    setProposalRefInput('');
    setCartIdInput('');
    setStartDatePicker(null);
    setEndDatePicker(null);

    const clearedButtons = buttons.map((button) => ({
      ...button,
      isSelected: button.initials === InitialStatus.Todas,
    }));

    setButtons(clearedButtons);
  };

  const isBtnDisabled = () => {
    if (
      (startDateInput && endDateInput)
      || selectedButton
      || buttons.some((button) => button.isSelected)
    ) {
      return false;
    }
    return true;
  };

  return (
    <>
      <div className={classNames(styles.filterCalendarWrapper, styles.bodyWrapper)}>
        <div className={styles.titleFilter}>
          <IonLabel className={styles.titleFilterInfo}>Filtrar por ...</IonLabel>
        </div>
        <div className={styles.calendar}>
          <div className={styles.calendarTitle}>
            <IonLabel className={styles.calendarTitleInfo}>
              Selecciona una fecha
            </IonLabel>
          </div>
          <div className={styles.calendarDate}>
            <div
              className={classNames(styles.calendarDateStart, openCalendarStartDate ? styles.onfocusDatepicker : '')}
            >
              <IonIcon md={calendarClearOutline} className={styles.iconsCalendar} />
              <IonInput
                placeholder="Desde"
                onClick={() => {
                  setOpenCalendarStartDate(true);
                  setOpenCalendarEndDate(false);
                }}
                value={startDateInput ? DateHelper(startDateInput) : ''}
              />
            </div>
            <div className={styles.calendarDateSlash}>
              <IonText>-</IonText>
            </div>
            <div
              className={classNames(styles.calendarDateEnd, openCalendarEndDate ? styles.onfocusDatepicker : '')}
            >
              <IonIcon md={calendarClearOutline} className={styles.iconsCalendar} />
              <IonInput
                placeholder="Hasta"
                onClick={() => {
                  setOpenCalendarStartDate(false);
                  setOpenCalendarEndDate(true);
                }}
                value={endDateInput ? DateHelper(endDateInput) : ''}
              />
            </div>
          </div>
          <div className={styles.buttonsCalendarDate}>
            <IonButton
              className={
                selectedButton === 'today'
                  ? styles.buttonsCalendarTodaySelected
                  : styles.buttonsCalendarToday
              }
              onClick={() => handleToday()}
            >
              Hoy
            </IonButton>
            <IonButton
              className={
                selectedButton === 'currentMonth'
                  ? styles.buttonsCalendarCurrentmonthSelected
                  : styles.buttonsCalendarCurrentmonth
              }
              onClick={() => handleCurrentMonth()}
            >
              Mes corriente
            </IonButton>
            <IonButton
              className={
                selectedButton === 'lastMonth'
                  ? styles.buttonsCalendarLastmonthSelected
                  : styles.buttonsCalendarLastmonth
              }
              onClick={() => handleLastMonth()}
            >
              Mes anterior
            </IonButton>
          </div>
        </div>
        {(openCalendarStartDate || openCalendarEndDate) && (
          <Datepicker
            openCalendarStartDate={openCalendarStartDate}
            setOpenCalendarStartDate={setOpenCalendarStartDate}
            openCalendarEndDate={openCalendarEndDate}
            setOpenCalendarEndDate={setOpenCalendarEndDate}
            startDatePicker={startDatePicker}
            setStartDatePicker={setStartDatePicker}
            endDatePicker={endDatePicker}
            setEndDateInput={setEndDateInput}
            setEndDatePicker={setEndDatePicker}
            setStartDateInput={setStartDateInput}
          />
        )}
        <div className={styles.status}>
          <div className={styles.statusTitle}>
            <IonLabel className={styles.statusTitleInfo}>Estado</IonLabel>
          </div>
          <div className={styles.statusSelecteds}>
            {buttons.map(({ name, initials, isSelected }: Status) => (
              <div
                className={
                  isSelected ? styles.buttonStyleSelected : styles.buttonStyle
                }
                key={initials}
                id={initials}
                onClick={() => selectButtons(initials, !isSelected)}
              >
                <IonText className={isSelected ? styles.selectedText : ''}>
                  {name}
                </IonText>
              </div>
            ))}
          </div>
        </div>
        {existingEcoOffers && (
          <div className={styles.inputFilterWrapper}>
            <div className={styles.proposalReferenceInfo}>
              <IonLabel class={styles.proposalReferenceTitle}>ID carrito</IonLabel>
            </div>
            <IonInput
              className={styles.inputFilter}
              placeholder="Buscar por ID carrito"
              onIonInput={(event: CustomEvent<InputChangeEventDetail>) => {
                setCartIdInput(event.detail.value ?? '');
              }}
              value={cartIdInput ?? ''}
            />
          </div>
        )}
        {cookieProposalReference && (
          <div className={styles.inputFilterWrapper}>
            <div className={styles.proposalReferenceInfo}>
              <IonLabel class={styles.proposalReferenceTitle}>Código AG</IonLabel>
            </div>
            <IonInput
              className={styles.inputFilter}
              placeholder="Buscar por código AG"
              onIonInput={(event: CustomEvent<InputChangeEventDetail>) => {
                setProposalRefInput(event.detail.value ?? '');
              }}
              value={proposalRefInput ?? ''}
            />
          </div>
        )}
      </div>
      <div className={classNames(styles.filterCalendarWrapper, styles.buttonsWrapper)}>
        {/* {(startDateInput && endDateInput) ||
        selectedButton ||
        buttons.some(
          (button) =>
            button.isSelected && button.initials !== InitialStatus.Todas
        ) ? ( */}
        <div className={styles.buttons}>
          <IonFooter className={styles.calendarFooter}>
            {/* <IonToolbar className="calendar-toolbar"> */}
            <IonButtons className={styles.buttonAplication}>
              <IonButton
                className={styles.buttonAplicationFilter}
                onClick={handleApliedFilter}
                disabled={isBtnDisabled()}
              >
                Aplicar Filtro
              </IonButton>
            </IonButtons>
            <IonButtons className={styles.buttonAplicationDeleted}>
              <IonButton
                className={styles.buttonAplicationFilter}
                onClick={clearFilters}
                disabled={isBtnDisabled()}
              >
                Borrar
              </IonButton>
            </IonButtons>
            {/* </IonToolbar> */}
          </IonFooter>
        </div>
      </div>
    </>
  );
}
